import isUndefined from 'lodash.isundefined';
import uniq from 'lodash.uniq';
import uniqBy from 'lodash.uniqby';
import { useState, useEffect } from 'react';
import productTypes from '@general/productTypes';
import useEventToRegisterSessionsFor from '@hooks/useEventToRegisterSessionsFor';
import useViewer from '@hooks/useViewer';

const useCartItemsGroupedByMember = ({ cart, itemCount, optionalFullProgramEvents, total }) => {
    const [itemsGroupedByMember, setItemsGroupedByMember] = useState();
    const { viewer } = useViewer();
    const { eventToRegisterSessionsFor } = useEventToRegisterSessionsFor();

    useEffect(() => {
        if (!cart || !viewer || isUndefined(eventToRegisterSessionsFor)) {
            return;
        }

        const itemsGroupedByMemberWorking = [];
        const memberIds = uniq(cart.items.map(item => item.member_id));

        memberIds.forEach(memberId => {
            const itemsForMember = cart.items.filter(item => item.member_id === memberId);
            const membershipItems = {
                type: productTypes.MEMBERSHIP,
                items: [],
            };

            const eventRegistrationItems = {
                type: productTypes.EVENT,
                items: [],
            };

            const optionalEventsForMember = optionalFullProgramEvents
                ? optionalFullProgramEvents.filter(event => event.memberId === memberId)
                : [];

            itemsForMember.forEach(item => {
                if (item.product_type === productTypes.MEMBERSHIP) {
                    // There will only ever be a single membership item per member
                    membershipItems.items.push({
                        product_id: item.product_id,
                        name: item.description,
                        memberId,
                        dues: itemsForMember.filter(i =>
                            [
                                productTypes.MEMBERSHIP,
                                productTypes.PAC,
                                productTypes.SIC,
                                productTypes.FOUNDATION_DONATION,
                            ].includes(i.product_type)
                        ),
                    });

                    return;
                }

                if (item.product_type === productTypes.EVENT) {
                    const code = item.meta_info.event_code;
                    const name = item.meta_info.event_name;
                    eventRegistrationItems.items.push({
                        product_id: item.product_id,
                        code,
                        name,
                        memberId,
                        sessions: itemsForMember.filter(i => i.meta_info?.event_code === code),
                    });
                }

                if (item.product_type === productTypes.EVENT_SESSION) {
                    // This logic handles the scenario where a user is already registered for an event
                    // but wants to purchase sessions after the fact.
                    const eventCode = item.meta_info.event_code;
                    const fullProgramRegistration = itemsForMember.find(
                        i => i.product_type === productTypes.EVENT && i.meta_info.event_code === eventCode
                    );

                    if (fullProgramRegistration) {
                        // If member is registered for FP for the session, ignore this logic
                        return;
                    }

                    const existingItemForEvent = eventRegistrationItems.items.find(
                        eventItem => eventItem.code === eventCode
                    );

                    if (existingItemForEvent) {
                        // If event has already been added to the grouping, ignore this logic
                        return;
                    }

                    // Get all sessions for the event and add to the event group
                    const allSessionsForEvent = itemsForMember.filter(
                        i => i.product_type === productTypes.EVENT_SESSION && i.meta_info.event_code === eventCode
                    );

                    eventRegistrationItems.items.push({
                        product_id: eventCode,
                        code: eventCode,
                        name: allSessionsForEvent[0].meta_info.event_name,
                        memberId,
                        sessions: allSessionsForEvent,
                        sessionsOnly: true,
                    });
                }
            });

            if (optionalFullProgramEvents && optionalFullProgramEvents.length > 0 && !eventToRegisterSessionsFor) {
                eventRegistrationItems.items = uniqBy(
                    [...eventRegistrationItems.items, ...optionalEventsForMember],
                    'product_id'
                );
            }

            const itemsGroupedByType = [];
            if (membershipItems.items.length > 0) {
                itemsGroupedByType.push(membershipItems);
            }

            if (eventRegistrationItems.items.length > 0) {
                itemsGroupedByType.push(eventRegistrationItems);
            }

            itemsGroupedByMemberWorking.push({
                memberCompany: itemsForMember[0].meta_info.company_name,
                memberId,
                memberName: itemsForMember[0].meta_info.member_name,
                itemsGroupedByType,
            });
        });

        // Creates a cart for optional sessions when event code is pulled from the URL
        if (memberIds.length === 0 && eventToRegisterSessionsFor) {
            itemsGroupedByMemberWorking.push({
                memberId: viewer.recordId,
                memberName: viewer.fullName,
                itemsGroupedByType: [
                    {
                        type: productTypes.EVENT,
                        items: [eventToRegisterSessionsFor],
                    },
                ],
            });
        }

        // Creates a card for optional sessions when there is no event in the cart
        if (memberIds.length === 0 && optionalFullProgramEvents?.length > 0 && !eventToRegisterSessionsFor) {
            itemsGroupedByMemberWorking.push({
                memberId: viewer.recordId,
                memberName: viewer.fullName,
                itemsGroupedByType: [
                    {
                        type: productTypes.EVENT,
                        items: optionalFullProgramEvents,
                    },
                ],
            });
        }

        setItemsGroupedByMember(itemsGroupedByMemberWorking);
    }, [cart, itemCount, total, viewer, eventToRegisterSessionsFor, optionalFullProgramEvents]);

    return { itemsGroupedByMember };
};

export default useCartItemsGroupedByMember;
